import { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import {
  Dashboard,
  People,
  AccessTime,
  Assessment,
  EventNote,
  Receipt,
  Business,
  CalendarToday,
  ReceiptLong,
  Send,
  Settings,
  Logout,
  Menu,
  X,
  CheckCircle,
  Cancel,
  Notifications,
  AccountCircle,
  Edit,
  Visibility,
  Security,
  ArrowDropDown,
  BarChart,
  Description,
  Work,
  TrendingUp,
  Flag,
  History,
  Group,
} from "@mui/icons-material";
import {
  enrichLocationWithAddress,
  requestCurrentLocation,
} from "../utils/location";
import Avatar from "../components/ui/Avatar";
import NotificationBell from "../components/ui/NotificationBell";
import { hasNavPath } from "../utils/navPermissions";

const drawerWidth = 280;

const menuItems = [
  {
    text: "Dashboard",
    icon: Dashboard,
    path: "/dashboard",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Employees",
    icon: People,
    path: "/employees",
    roles: ["ADMIN", "HR", "MANAGER", "EMPLOYEE"],
  },
  {
    text: "Attendance",
    icon: AccessTime,
    path: "/attendance",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Attendance Report",
    icon: Assessment,
    path: "/attendance/report",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Leave",
    icon: EventNote,
    path: "/leave",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Expenses",
    icon: Receipt,
    path: "/expenses",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Departments",
    icon: Business,
    path: "/departments",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Holidays",
    icon: CalendarToday,
    path: "/holidays",
    roles: ["ADMIN", "MANAGER", "HR"],
  },
  {
    text: "Salary Slips",
    icon: ReceiptLong,
    path: "/salary-slip",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Send Salary Slips",
    icon: Send,
    path: "/payroll/send-salary-slip",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Payroll Status",
    icon: ReceiptLong,
    path: "/payroll/status",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Goals",
    icon: Flag,
    path: "/goals",
    roles: ["ADMIN", "MANAGER", "HR", "EMPLOYEE"],
  },
  {
    text: "My Activity",
    icon: History,
    path: "/activity",
    roles: ["ADMIN", "MANAGER", "HR", "EMPLOYEE"],
  },
  {
    text: "Team Activity",
    icon: Group,
    path: "/activity/team",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Performance",
    icon: TrendingUp,
    path: "/performance",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Document Vault",
    icon: Description,
    path: "/documents",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Recruitment",
    icon: Work,
    path: "/recruitment",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "HR Analytics",
    icon: BarChart,
    path: "/dashboard/analytics",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    text: "Role Management",
    icon: Security,
    path: "/role-management",
    roles: ["ADMIN"],
  },
  {
    text: "Nav Permissions",
    icon: Settings,
    path: "/nav-permissions",
    roles: ["ADMIN"],
  },
];

const toLocalDateParam = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const summarizeWorkedTime = (logs = []) => {
  if (!Array.isArray(logs) || logs.length === 0) {
    return { totalMs: 0, activeSince: null };
  }

  const sortedLogs = [...logs]
    .filter(
      (entry) => entry && entry.timestamp && typeof entry.action === "string"
    )
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

  let activeCheckIn = null;
  let totalMs = 0;

  sortedLogs.forEach((entry) => {
    const timestamp = new Date(entry.timestamp).getTime();
    if (Number.isNaN(timestamp)) {
      return;
    }

    const action = entry.action?.toLowerCase?.();

    if (action === "checkin") {
      activeCheckIn = timestamp;
    } else if (
      action === "checkout" &&
      activeCheckIn !== null &&
      timestamp > activeCheckIn
    ) {
      totalMs += timestamp - activeCheckIn;
      activeCheckIn = null;
    }
  });

  return {
    totalMs,
    activeSince: activeCheckIn,
  };
};

const formatDurationLabel = (milliseconds) => {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) {
    return "0h 00m 00s";
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes.toString().padStart(2, "0")}m ${seconds
    .toString()
    .padStart(2, "0")}s`;
};

export default function MainLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = user?._id || user?.id;
  const mainRef = useRef(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [todayHours, setTodayHours] = useState("0h 00m 00s");
  const [todayHoursLoading, setTodayHoursLoading] = useState(false);
  const [todayWorkSnapshot, setTodayWorkSnapshot] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  /** Same as dashboard "Total Users" (GET /dashboard/stats totalUsers). */
  const [employeesNavCount, setEmployeesNavCount] = useState(null);

  const checkAttendanceStatus = useCallback(async () => {
    if (!userId) {
      setCheckedIn(false);
      setTodayHours("0h 00m 00s");
      setTodayWorkSnapshot(null);
      return;
    }

    setTodayHoursLoading(true);

    try {
      const todayParam = toLocalDateParam(new Date());
      const todaysResponse = await api.get("/attendance/logs", {
        params: {
          userId,
          date: todayParam,
        },
      });

      const todaysLogs = Array.isArray(todaysResponse.data)
        ? todaysResponse.data
        : Array.isArray(todaysResponse.data?.data)
        ? todaysResponse.data.data
        : [];

      let latestRecords = todaysLogs;

      if (latestRecords.length === 0) {
        const latestFallback = await api.get("/attendance/logs", {
          params: {
            userId,
            limit: 1,
          },
        });

        latestRecords = Array.isArray(latestFallback.data)
          ? latestFallback.data
          : Array.isArray(latestFallback.data?.data)
          ? latestFallback.data.data
          : [];
      }

      const { totalMs, activeSince } = summarizeWorkedTime(todaysLogs);

      // Determine checked-in status:
      // 1. Check if there's an active session (activeSince is not null)
      // 2. Also check if total hours >= 9 (auto-checkout threshold)
      // 3. If hours >= 9, user should be checked out even if logs say otherwise
      const totalHours = totalMs / (1000 * 60 * 60);
      const hasActiveSession = activeSince !== null;
      const shouldBeCheckedOut = totalHours >= 9;

      // User is checked in only if:
      // - There's an active session (activeSince !== null)
      // - AND total hours < 9 (not auto-checked out)
      const isCheckedIn = hasActiveSession && !shouldBeCheckedOut;

      console.log("📊 Attendance status check:", {
        totalHours: totalHours.toFixed(2),
        hasActiveSession,
        shouldBeCheckedOut,
        isCheckedIn,
        latestLogAction: latestRecords[0]?.action,
      });

      setCheckedIn(isCheckedIn);
      setTodayWorkSnapshot({
        totalMs,
        activeSince: shouldBeCheckedOut ? null : activeSince,
      });
    } catch (err) {
      console.error("Failed to check attendance status", err);
      setTodayWorkSnapshot(null);
      setTodayHours("--");
      setCheckedIn(false);
    } finally {
      setTodayHoursLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkAttendanceStatus();

    // Check every 5 minutes for auto-checkout (if 9 hours completed)
    const interval = setInterval(() => {
      checkAttendanceStatus();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [checkAttendanceStatus]);

  useEffect(() => {
    if (!todayWorkSnapshot) {
      return undefined;
    }

    let autoCheckoutNotified = false;

    const tick = () => {
      const baseMs = Number.isFinite(todayWorkSnapshot.totalMs)
        ? todayWorkSnapshot.totalMs
        : 0;
      const runningMs = todayWorkSnapshot.activeSince
        ? Math.max(0, Date.now() - todayWorkSnapshot.activeSince)
        : 0;
      const totalMs = baseMs + runningMs;
      const totalHours = totalMs / (1000 * 60 * 60);

      setTodayHours(formatDurationLabel(totalMs));

      // Check if 9 hours completed and auto-checkout
      if (totalHours >= 9 && checkedIn && !autoCheckoutNotified) {
        autoCheckoutNotified = true;
        toast.info("You've completed 9 hours! Auto-checking out...", {
          autoClose: 5000,
        });
        // Trigger status check which will detect auto-checkout
        setTimeout(() => {
          checkAttendanceStatus();
        }, 2000);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [todayWorkSnapshot, checkedIn, checkAttendanceStatus]);

  useEffect(() => {
    const handleAttendanceUpdated = (event) => {
      const record = event?.detail;
      if (record?.action === "checkin") {
        setCheckedIn(true);
      } else if (record?.action === "checkout") {
        setCheckedIn(false);
      }

      checkAttendanceStatus();
    };

    window.addEventListener("attendanceUpdated", handleAttendanceUpdated);
    return () =>
      window.removeEventListener("attendanceUpdated", handleAttendanceUpdated);
  }, [checkAttendanceStatus]);

  // Scroll main content area to top on every route change
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // Normalize role to uppercase for comparison
  const userRole = user?.role?.toUpperCase() || "EMPLOYEE";

  const filteredMenuItems = menuItems.filter((item) => {
    const normalizedRoles = item.roles.map((r) => r.toUpperCase());
    return normalizedRoles.includes(userRole);
  });

  // Apply per-user navPermissions override (if set by admin/manager)
  const navMenuItems = (() => {
    const navPerms = user?.navPermissions;
    // Explicit custom config: pick from ALL menu items regardless of role.
    // Admin can grant any item to any user — navPermissions is the sole authority.
    if (Array.isArray(navPerms)) {
      return menuItems.filter((item) => hasNavPath(user, item.path));
    }
    // Dashboard + Employees are allocation-only for EMPLOYEE/HR.
    // ADMIN/MANAGER keep role-default visibility.
    const role = (user?.role || "").toUpperCase();
    if (role === "EMPLOYEE" || role === "HR") {
      return filteredMenuItems.filter(
        (item) => item.path !== "/dashboard" && item.path !== "/employees"
      );
    }
    return filteredMenuItems;
  })();

  const employeesNavKey = navMenuItems.map((i) => i.path).join("|");

  useEffect(() => {
    if (!user?.id) {
      setEmployeesNavCount(null);
      return;
    }
    if (!navMenuItems.some((i) => i.path === "/employees")) {
      setEmployeesNavCount(null);
      return;
    }
    let cancelled = false;
    api
      .get("/dashboard/stats")
      .then((res) => {
        const n = res.data?.totalUsers ?? res.data?.totalEmployees;
        if (!cancelled && typeof n === "number") setEmployeesNavCount(n);
      })
      .catch(() => {
        if (!cancelled) setEmployeesNavCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, employeesNavKey]);

  // Show attendance widgets only when the user actually has access to /attendance
  const hasAttendanceAccess = navMenuItems.some((item) => item.path === "/attendance");

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const ensureGeolocationPermission = async () => {
    if (typeof navigator === "undefined") {
      toast.error("Attendance actions require a browser environment.");
      return false;
    }

    if (!("geolocation" in navigator)) {
      toast.error("Geolocation is not supported on this device.");
      return false;
    }

    if (!navigator.permissions?.query) {
      return true;
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });
      if (permissionStatus.state === "denied") {
        toast.error(
          "Location permission is required to record attendance. Please enable it and try again."
        );
        return false;
      }
      return true;
    } catch (err) {
      console.warn("Failed to check geolocation permission", err);
      return true;
    }
  };

  const handleSimpleAction = async (action) => {
    setPendingAction(action);
    setActionLoading(true);
    try {
      const hasPermission = await ensureGeolocationPermission();
      if (!hasPermission) {
        return;
      }

      const location = await requestCurrentLocation({
        desiredAccuracy: 30,
        improvementTimeout: 12000,
      });
      const enrichedLocation = await enrichLocationWithAddress(location);
      const response = await api.post(`/attendance/logs/${action}`, {
        location: enrichedLocation,
      });
      const record = response.data;

      if (action === "checkin") {
        toast.success("Checked in successfully!");
        setCheckedIn(true);
      } else {
        toast.success("Checked out successfully!");
        setCheckedIn(false);
      }

      window.dispatchEvent(
        new CustomEvent("attendanceUpdated", { detail: record })
      );
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || `Failed to ${action}`;
      toast.error(message);
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Top Navbar - Full Width */}
      <nav className="sticky top-0 z-30 bg-primary border-b border-primary/20 shadow-soft w-full">
        <div
          className="px-4 sm:px-6 py-2 flex items-center justify-between"
          style={{ minHeight: "62px" }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 rounded-xl hover:bg-primary/20 transition-colors"
            >
              <Menu className="text-white w-5 h-5" />
            </button>
            {/* Logo */}
            <div
              className="hidden lg:flex items-center gap-3 flex-nowrap"
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                src="/new_tablecloth.png"
                alt="HRMS Logo"
                className="flex-shrink-0"
                style={{
                  display: "block",
                  width: "201px",
                  height: "62px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasAttendanceAccess && (
              <>
                {/* Today's Hours */}
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-xl">
                  <AccessTime className="text-white w-4 h-4" />
                  {todayHoursLoading ? (
                    <div className="w-16 h-3 bg-white/20 skeleton rounded" />
                  ) : (
                    <span className="text-xs font-semibold text-white">
                      {todayHours}
                    </span>
                  )}
                </div>

                {/* Check In/Out Button */}
                {!checkedIn ? (
                  <motion.button
                    onClick={() => handleSimpleAction("checkin")}
                    disabled={actionLoading || !userId}
                    className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white rounded-xl text-xs font-semibold hover:bg-green-600 transition-colors disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {actionLoading && pendingAction === "checkin" ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Check In</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={() => handleSimpleAction("checkout")}
                    disabled={actionLoading || !userId}
                    className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-xl text-xs font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {actionLoading && pendingAction === "checkout" ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <Cancel className="w-4 h-4" />
                        <span>Check Out</span>
                      </>
                    )}
                  </motion.button>
                )}

                {/* Notifications */}
                <NotificationBell />
              </>
            )}

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-primary/20 transition-colors"
              >
                <Avatar src={user?.profile_image} name={user?.name} size="sm" />
                <span className="hidden md:block text-sm font-semibold text-white">
                  {user?.name}
                </span>
                <ArrowDropDown className="text-white w-4 h-4" />
              </button>

              {/* Profile Dropdown Menu */}
              <AnimatePresence>
                {profileMenuOpen && (
                  <>
                    <motion.div
                      className="fixed inset-0 z-40"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <motion.div
                      className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-200 z-50 overflow-hidden"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate("/profile/view");
                            setProfileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition-colors text-left"
                        >
                          <Visibility className="w-5 h-5 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">
                            View Profile
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            navigate("/profile");
                            setProfileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition-colors text-left"
                        >
                          <Edit className="w-5 h-5 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Edit Profile
                          </span>
                        </button>
                        {(user?.role === "admin" || user?.role === "ADMIN") && (
                          <button
                            onClick={() => {
                              navigate("/settings");
                              setProfileMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition-colors text-left"
                          >
                            <Settings className="w-5 h-5 text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Settings
                            </span>
                          </button>
                        )}
                        {hasAttendanceAccess && (
                          <button
                            onClick={async () => {
                              setProfileMenuOpen(false);
                              try {
                                const permissionStatus =
                                  await navigator.permissions.query({
                                    name: "geolocation",
                                  });
                                if (permissionStatus.state === "denied") {
                                  toast.error(
                                    "Location permission is denied. Please enable it in your browser settings to use attendance features."
                                  );
                                } else if (permissionStatus.state === "prompt") {
                                  toast.info(
                                    "Please allow location access when prompted to use attendance features."
                                  );
                                } else {
                                  toast.success(
                                    "Location permission is granted. You can use attendance features."
                                  );
                                }
                              } catch (err) {
                                toast.info(
                                  "Click 'Check In' to grant location permission."
                                );
                              }
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition-colors text-left"
                          >
                            <Security className="w-5 h-5 text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">
                              Permissions
                            </span>
                          </button>
                        )}
                        <div className="border-t border-gray-200 my-1" />
                        <button
                          onClick={() => {
                            setProfileMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 transition-colors text-left"
                        >
                          <Logout className="w-5 h-5 text-red-500" />
                          <span className="text-sm font-medium text-red-600">
                            Logout
                          </span>
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Layout - Sidebar + Content */}
      <div className="flex flex-1 min-w-0 min-h-0">
        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileOpen(false)}
              />
              <motion.div
                className="fixed left-0 w-72 bg-white z-50 lg:hidden shadow-2xl"
                style={{ top: "calc(62px + 0.5rem)", bottom: 0 }}
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
              >
                {/* Mobile Sidebar Content */}
                <SidebarContent
                  user={user}
                  navMenuItems={navMenuItems}
                  employeesNavCount={employeesNavCount}
                  hasAttendanceAccess={hasAttendanceAccess}
                  location={location}
                  handleNavigation={handleNavigation}
                  handleLogout={handleLogout}
                  checkedIn={checkedIn}
                  actionLoading={actionLoading}
                  pendingAction={pendingAction}
                  handleSimpleAction={handleSimpleAction}
                  userId={userId}
                  todayHours={todayHours}
                  todayHoursLoading={todayHoursLoading}
                  onClose={() => setMobileOpen(false)}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-72 bg-white border-r border-gray-200">
          <SidebarContent
            user={user}
            navMenuItems={navMenuItems}
            employeesNavCount={employeesNavCount}
            hasAttendanceAccess={hasAttendanceAccess}
            location={location}
            handleNavigation={handleNavigation}
            handleLogout={handleLogout}
            checkedIn={checkedIn}
            actionLoading={actionLoading}
            pendingAction={pendingAction}
            handleSimpleAction={handleSimpleAction}
            userId={userId}
            todayHours={todayHours}
            todayHoursLoading={todayHoursLoading}
          />
        </aside>

        {/* Main Content */}
        <main ref={mainRef} className="flex-1 min-w-0 p-4 sm:p-6 overflow-auto">
          <motion.div
            className="min-w-0 w-full max-w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

function navItemIsActive(itemPath, pathname) {
  if (pathname === itemPath) return true;
  if (itemPath === "/goals" && pathname.startsWith("/goals")) return true;
  return false;
}

function SidebarContent({
  user,
  navMenuItems,
  employeesNavCount,
  hasAttendanceAccess,
  location,
  handleNavigation,
  handleLogout,
  checkedIn,
  actionLoading,
  pendingAction,
  handleSimpleAction,
  userId,
  todayHours,
  todayHoursLoading,
  onClose,
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Today's Hours - Mobile */}
      {hasAttendanceAccess && (
        <div className="lg:hidden px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3 px-4 py-3 bg-primary/5 rounded-2xl">
            <AccessTime className="text-primary w-5 h-5" />
            {todayHoursLoading ? (
              <div className="w-20 h-4 skeleton rounded" />
            ) : (
              <span className="text-sm font-semibold text-primary">
                {todayHours}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="space-y-1">
          {navMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = navItemIsActive(item.path, location.pathname);
            const showEmpCount =
              item.path === "/employees" && typeof employeesNavCount === "number";
            return (
              <motion.button
                key={item.path}
                onClick={() => {
                  handleNavigation(item.path);
                  onClose?.();
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-medium transition-all ${
                  isActive
                    ? "bg-primary text-white shadow-soft"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left min-w-0">{item.text}</span>
                {showEmpCount && (
                  <span
                    className={`flex-shrink-0 text-xs font-bold tabular-nums px-2 py-0.5 rounded-full ${
                      isActive
                        ? "bg-white/25 text-white"
                        : "bg-primary/10 text-primary"
                    }`}
                  >
                    {employeesNavCount}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      </nav>

      {/* Mobile Check In/Out */}
      {hasAttendanceAccess && (
        <div className="lg:hidden px-4 pb-4 border-t border-gray-200 pt-4">
          {!checkedIn ? (
            <motion.button
              onClick={() => {
                handleSimpleAction("checkin");
                onClose?.();
              }}
              disabled={actionLoading || !userId}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-2xl font-semibold hover:bg-green-600 transition-colors disabled:opacity-50"
              whileTap={{ scale: 0.98 }}
            >
              {actionLoading && pendingAction === "checkin" ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Check In</span>
                </>
              )}
            </motion.button>
          ) : (
            <motion.button
              onClick={() => {
                handleSimpleAction("checkout");
                onClose?.();
              }}
              disabled={actionLoading || !userId}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-2xl font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
              whileTap={{ scale: 0.98 }}
            >
              {actionLoading && pendingAction === "checkout" ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Cancel className="w-5 h-5" />
                  <span>Check Out</span>
                </>
              )}
            </motion.button>
          )}
        </div>
      )}
    </div>
  );
}
